import React, {useState, useEffect, useCallback, ChangeEvent, ProgressHTMLAttributes, useMemo} from 'react';
import {useParams} from 'react-router-dom';
import Video from '@src/ds/components/molecules/Video/Video';
import Hls from 'hls.js';
import EmbedStyl from './Embed.styl';

import {AIProductsResponse} from '@src/Data/Model/AIProducts';
import AIProducsService from '@src/Data/DataSource/AIProducts';
import MindMap from '@src/ds/components/molecules/MindMap/MindMap';
import SummaryText from '@src/ds/components/molecules/SummaryText/SummaryText';
import Quiz from '@src/ds/components/molecules/Quiz/Quiz';
import Search from '@src/ds/components/molecules/Search/SearchComponent';

import ClickableArea from './components/ClickableArea';
import Loader from '@src/ds/components/atoms/Loader';
import ControlBar from './components/ControlBar';
import { handleEventVideo } from '@src/ds/Utils/VideoUtils';
import { Chapter } from './components/Seekbar';


function isAirplay() {
  return (
    (!!(window as any)?.WebKitPlaybackTargetAvailabilityEvent) ||
    !!(
      (!!(window as any).chrome?.cast?.isAvailable) ||
      (!!(navigator as any).presentation?.receiver)
    )
  ) as boolean;
}

// import {VideoResponse} from '@src/Data/Model/Video';
// import DataSourceMain from '@src/Data/DataSource/Video';
// import HttpClient from '@Infra/Http';
// import Loader from '@src/ds/components/atoms/Loader';
// import {PageLoader} from '@src/ds/components/atoms/Loader/Loader.styl';

declare global {
  interface HTMLVideoElement {
    webkitCurrentPlaybackTargetIsAirPlay?: boolean;
  }
}

export interface CTAs {
  startTime: number;
  endTime: number;
  text: string;
  link: string;
  bgColor: string;
  textColor: string;
  position: 'top-left' | 'top-right' | 'top-center' | 'mid-left' | 'mid-right' | 'mid-center' | 'bottom-left' | 'bottom-right' | 'bottom-center';
  size: 'sm' | 'md' | 'lg';
}

export interface Attachments {
  title: string;
  link: string;
}

export type Embed2Props = {
  code?: string;
};

export type EmbedData = {
  safeVideo: boolean;
  primaryColor: string;
  textColor: string;
  transcription: string;
  subtitles: {
    label: string;
    src: string;
    language: string;
  }[];
  subtitlesHls: {
    label: string;
    src: string;
    language: string;
  }[];
  config: {
    safeVideo: boolean;
    progressBar: boolean;
    showConfigurations: boolean;
    volume: boolean;
    logo: boolean;
    keepWatching: boolean;
    airplay: boolean;
    chromecast: boolean;
    bigPlay: boolean;
    autoplay: boolean;
    fullScreen: boolean;
    muted: boolean;
    responsive: boolean;
    preload: boolean;
    loop: boolean;
    pip: boolean;
    controls: boolean;
    originalAspectRatio: boolean;
    emptyControlBar: boolean;
    progressBarDumby?: boolean;
    skipIntro?: number | null;
  };
  aspectRatio: string;
  poster: string;
  video: string;
  token: string;
  cdnKey: string;
  summaryVideo: string;
  summaryVideoCdnKey: string;
  authorization: string;
  waterMark: string;
  ai: string;
  code: string;
  name: string;
  live: boolean;
  chapters: Chapter[];
  enableCta: boolean;
  cta: CTAs[];
  attachments: Attachments[];
} | null;

export type Level = {
  index: number;
  id: string;
  bitrate: number;
  width: number;
  height: number;
};

export type StreamLevels = Array<Level>;

const Embed2 = ({ code }: Embed2Props) => {

  const [canAirplay] = React.useState<boolean>(isAirplay());
  const [isAirplayAvailable, setIsAirplayAvailable] = React.useState<boolean>(false);
  const [isAirplayConnected, setIsAirplayConnected] = React.useState<boolean>(false);

  const [isBuffering, setIsBuffering] = React.useState<boolean>(true);
  const [currentLevel, setCurrentLevel] = React.useState<number>(0);
  const [levels, setLevels] = React.useState<StreamLevels>([]);

  const [duration, setDuration] = React.useState<number>(0);
  const videoEl = React.useRef(null);
  const videoContainer = React.useRef<HTMLDivElement | null>(null);

  const [openSummary, setOpenSummary] = React.useState<boolean>(false);
  const [openSummaryText, setOpenSummaryText] = React.useState<boolean>(false);
  const [openMindMap, setOpenMindMap] = React.useState<boolean>(false);
  const [openQuiz, setOpenQuiz] = React.useState<boolean>(false);
  const [openSearch, setOpenSearch] = React.useState<boolean>(false);


  const [data, setData] = useState<EmbedData>(null);
  const [hls, setHls] = React.useState<any>(null);
  const [aiProducts, setAIProducts] = React.useState<AIProductsResponse | null>(null);

  // Exemplo de capítulos dummy para teste
  const chapters: Chapter[] = data?.chapters || [];
  const ctas: CTAs[] = data?.cta || [];
  const attachments: Attachments[] = data?.attachments || [];

  const safeVideo = data?.config?.safeVideo;

  // if (isLoading) {
  //   return (
  //     <PageLoader className="page-loader--studio">
  //       <Loader model="inline" variant="blue" />
  //     </PageLoader>
  //   );
  // }

  // <Video
  //   id="embed-player"
  //   primaryColor={data?.primaryColor || ''}
  //   transcription={data?.transcription || ''}
  //   subtitles={data?.subtitles || []}
  //   config={data?.config || {
  //     autoplay: false,
  //     fullScreen: false,
  //     muted: false,
  //     responsive: true,
  //     preload: true,
  //     loop: false,
  //     pip: true,
  //     controls: true,
  //     originalAspectRatio: false,
  //   }}
  //   aspectRatio={data?.aspectRatio || '16:9'}
  //   src={data?.video || ''}
  //   poster={data?.poster || ''}
  //   token={data?.token || ''}
  //   cdnKey={data?.cdnKey || ''}
  //   summaryVideo={data?.summaryVideo || ''}
  //   summaryVideoCdnKey={data?.summaryVideoCdnKey || ''}
  //   mediaCode={id || ''}
  //   ai={(data?.ai || '')}
  //   hasSearch={(data?.ai || '').indexOf('SMART_SEARCH') > -1}
  //   waterMark={data?.waterMark}
  //   authorization={data?.authorization || ''}
  // />

  function getColor(hexColor: string) {
    hexColor = hexColor.replace('#', '');
    const r = parseInt(hexColor.substring(0,2),16);
    const g = parseInt(hexColor.substring(2,2),16);
    const b = parseInt(hexColor.substring(4,2),16);
    const yiq = ((r*299)+(g*587)+(b*114))/1000;
    return (yiq >= 128) ? '#000' : '#fff';
  }

  async function getSource() {
    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    const dataAttr = rootElement.getAttribute('data');
    // const worker = rootParams.getAttribute('data-worker');
    // const ssrVideo = rootParams.getAttribute('data-video');
    // const posterVideo = rootParams.getAttribute('data-poster');
    // const tokenVideo = rootParams.getAttribute('data-id');

    // console.log('worker', worker, 'hasServiceWorker', hasServiceWorker);

    // setWorker(worker === '1');
    if (dataAttr && dataAttr !== '[DATA]') {
      const newData = JSON.parse(dataAttr.split("'").join('"') || '');
      const aspectRatio = newData?.config && newData?.config?.originalAspectRatio ? newData?.aspectRatio : '16/9';
      rootElement.style.aspectRatio = (aspectRatio).toString();
      rootElement.style.setProperty('--aspect-ratio', (aspectRatio).toString().replace(':', '/'));
      rootElement.style.setProperty('--primary-color', (data?.primaryColor || '#444CE7').toString());
      rootElement.style.setProperty('--text-color', (getColor(data?.primaryColor || '')).toString());

      // newData.config.autoplay = true;
      // newData.config.muted = true;
      setData({
        ...newData,
        textColor: getColor(newData?.primaryColor),
      });
      // setIsLoading(false);
      // return null;
    }

    // if (dataAttr
    // && dataAttr !== '[DATA]'
    // && hasServiceWorker
    // && worker === '1') {
    //   setIsLoading(false);
    // }

    // if (!hasServiceWorker || worker === '0') {
    //   setIsLoading(false);
    // }

    // const referrer = document?.referrer ? new URL(document?.referrer) : null;

    // const response: VideoResponse = await dataSource.getMedia({
    //   mediaCode: id,
    //   code: referrer?.origin ? window.btoa(referrer?.origin) : window.btoa('https://player.scaleup.com.br'),
    // });

    // setPoster(response.success ? (response?.poster || '') : '');
    // setSource(response.success ? response?.video : '');
    // setIsLoading(false);

    // return null;
  }

  async function onLoadAI() {
    if (!data?.code || !data?.authorization) {
      return;
    }

    if (data?.ai === '' || data?.ai === null) {
      return;
    }

    const result = await AIProducsService({
      mediaCode: data?.code,
      authorization: data?.authorization,
      products: (data?.ai || '').split(',').filter((item) => ['MIND_MAP', 'SUMMARY', 'SUMMARY_TEXT', 'QUESTION'].includes(item)),
    });

    if (result.success) {
      setAIProducts(result);
    }
  }

  useEffect(() => {
    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;

    const loadEvent = () => {
      getSource();
    };

    rootElement.addEventListener('load', loadEvent, false);

    return () => {
      rootElement.removeEventListener('load', loadEvent);
    }
  }, [code]);

  useEffect(() => {
    // if (hasServiceWorker && navigator.serviceWorker?.controller?.state === 'activated') {
    getSource();
    // }
  }, []);


  const isPlayerBuffering = useCallback(() => {
    // console.log('isPlayerBuffering');

    if (!isBuffering) {
      setIsBuffering(true);
    }
  }, [isBuffering])

  const isPlayerNotBuffering = useCallback(() => {
    // console.log('isPlayerNotBuffering');

    if (isBuffering) {
      setIsBuffering(false);
    }
  }, [isBuffering])

  useEffect(() => {
    if (!hls || !hls.media) {
      return;
    }

    hls.media.addEventListener('waiting', isPlayerBuffering);

    hls.media.addEventListener('playing', isPlayerNotBuffering);

    hls.media.addEventListener('canplay', isPlayerNotBuffering);

    // hls.media.addEventListener('seeked', isPlayerBuffering);

    return () => {
      hls.media.removeEventListener('waiting', isPlayerBuffering);

      hls.media.removeEventListener('playing', isPlayerNotBuffering);

      hls.media.removeEventListener('canplay', isPlayerNotBuffering);

      // hls.media.removeEventListener('seeked', isPlayerBuffering);
    }
  }, [data, hls, hls?.media, isPlayerBuffering, isPlayerNotBuffering]);

  useEffect(() => {
    if (!data) {
      return;
    }

    if (hls) {
      return;
    }

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    const videoEl = rootElement.querySelector('video') as HTMLVideoElement;

    // if (!!videoEl.canPlayType('application/vnd.apple.mpegurl') && isIOS()) {
    //   setHls({
    //     native: true,
    //     media: videoEl,
    //   });
    // } else {
      const newHlsInstance = new Hls({
        autoStartLoad: true,
        backBufferLength: 90,
        debug: false,
        // debug: true,
        enableWorker: true,
        workerPath: '/hls.worker.js',
        enableWebVTT: true,
        lowLatencyMode: true,
        // progressive: true,
        maxBufferLength: data?.config?.preload ? 60 : 10,
        maxMaxBufferLength: data?.config?.preload ? 60 : 10,
        frontBufferFlushThreshold: data?.config?.preload ? 30 : 60,
        // preferManagedMediaSource: false,

        // renderTextTracksNatively: false,
        xhrSetup: (xhr: any, url: string) => {
          if ((url).toString().indexOf('https://stream.scaleup.com.br/player/v1/playlists') > -1) {
            // url = (url).toString().indexOf('&platform=') > -1 ? url : `${url}&platform=PLAYER`;

            // if ((window as any).isDesktopChrome) {
              // xhr.setRequestHeader('X-Player-Request', '1');
              xhr.setRequestHeader('Authorization', 'Bearer '+ (data?.authorization || ''));
            // }

            xhr.open('GET', url, true);
          }
          else if ((url).toString().indexOf('https://stream.smartplayer.io') > -1 && (url).indexOf('tokenTrash') > -1) {

            // if ((window as any).isDesktopChrome) {
              // xhr.setRequestHeader('X-Player-Request', '1');
            // } else {
            url = (url).replace('tokenTrash', 'token').replace('expiresTrash', 'expires');
            // }

            xhr.open('GET', url, true);
          }
        },
      });

      (newHlsInstance as any).native = false;

      setHls(newHlsInstance);
    // }
  }, [data]);

  useEffect(() => {
    if (!hls) {
      return;
    }

    if (!videoEl.current || !videoContainer?.current) {
      return;
    }

    if (!hls.native) {
      hls.attachMedia(videoEl.current);

      hls.on(Hls.Events.MEDIA_ATTACHED, (event: any) => {
        if (!videoContainer?.current) {
          return;
        }

        // console.log('MEDIA_ATTACHED', event);

        hls.loadSource(hls.url ? hls.url : data?.video);

        const newLevels = hls.levels.map((level: any, index: number) => ({
          key: index,
          id: level.id,
          ...level.attrs,
          height: level.height,
        }));

        setLevels(newLevels);
      });

      hls.on(Hls.Events.LEVEL_LOADED, (eventType: any, eventValues: any) => {
        hls.config.maxBufferLength = 60;

        setDuration(eventValues.details.totalduration);
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        // console.log('MANIFEST_PARSED');

        const newLevels = hls.levels.map((level: any, index: number) => ({
          key: index,
          id: level.id,
          ...level.attrs,
          height: level.height,
          bitrate: level.bitrate,
        }));

        if (safeVideo) {
          const maxSafeVideoHeight = 720;
          let maxSafeVideoLevel = -1;

          for (let i = 0; i < hls.levels.length; i++) {
            if (hls.levels[i].height <= maxSafeVideoHeight) {
              maxSafeVideoLevel = i;
            } else {
              break;
            }
          }

          if (maxSafeVideoLevel >= 0) {
            hls.autoLevelCapping = maxSafeVideoLevel;
          }
        }

        setData({
          ...data,
          subtitlesHls: hls.allSubtitleTracks.map((subtitle: any) => ({
            label: subtitle.name,
            src: subtitle.url,
            language: subtitle.lang,
          })),
        } as typeof data);

        setLevels(newLevels);

        if (videoEl.current) {

          const parameters = urlParameters || {};

          if (Object.keys(parameters).includes('seekTo')) {
            (videoEl.current as any).currentTime = (parseInt(parameters.seekTo));
          }

          handleEventVideo('ready', videoEl.current as HTMLVideoElement);
        }
      });

      hls.on(Hls.Events.LEVEL_SWITCHING, function (eventName: string, data: any) {
        // console.log('LEVEL_SWITCHING');

        if (data.level === -1) {
          return;
        }

        setCurrentLevel(data.level);
      });
    }

    hls.media.addEventListener('play', () => {
      if (videoContainer?.current) {
        videoContainer?.current.classList.remove('smartplayer--ended');
        videoContainer?.current.classList.remove('smartplayer--paused');
        videoContainer?.current.classList.add('smartplayer--playing');
      }
    });

    hls.media.addEventListener('pause', () => {
      if (videoContainer?.current) {
        videoContainer?.current.classList.remove('smartplayer--ended');
        videoContainer?.current.classList.remove('smartplayer--playing');
        videoContainer?.current.classList.add('smartplayer--paused');
      }
    });

    hls.media.addEventListener('ended', () => {
      if (videoContainer?.current) {
        videoContainer?.current.classList.remove('smartplayer--playing');
        videoContainer?.current.classList.remove('smartplayer--paused');
        videoContainer?.current.classList.add('smartplayer--ended');
      }
    });

    if (hls.native && data?.live) {
      hls.on(Hls.Events.ERROR, (event: any, data: any) => {
        if (data?.type === Hls.ErrorTypes.NETWORK_ERROR
          && data?.details === 'manifestLoadError'
        ) {
          setTimeout(() => {
            hls.loadSource(hls.url ? hls.url : data?.video);
            hls.startLoad();
          }, 10000);
        }
      });
    }

    if (data?.summaryVideo) {
      videoContainer?.current.classList.add('smartplayer--video-with-summary');
    } else {
      videoContainer?.current.classList.add('smartplayer--video-without-summary');
    }

    if (data?.config?.autoplay) {
      try {
        hls.media.play().then(() => {
          if (videoContainer?.current) {
            videoContainer?.current.classList.add('smartplayer--playing');

            if (hls.media.muted) {
              videoContainer?.current.classList.add('smartplayer--autoplaying');
            }
          }
        }).catch((e: any) => {
          if (videoContainer?.current) {
            videoContainer?.current.classList.add('smartplayer--paused');
          }

          hls.media.volume = 0;
          hls.media.muted = true;
          // hls.media.dispatchEvent(new Event('volumechange'));
          hls.media.play().then(() => {
            if (videoContainer?.current) {
              videoContainer?.current.classList.add('smartplayer--playing');
              videoContainer?.current.classList.add('smartplayer--autoplaying');
            }
          });
        });
      } catch (error) {
        try {
          hls.media.volume = 0;
          hls.media.muted = true;
          hls.media.play().then(() => {
            if (videoContainer?.current) {
              videoContainer?.current.classList.add('smartplayer--playing');
              videoContainer?.current.classList.add('smartplayer--autoplaying');
            }
          });
        } catch (anotherError) {
          videoContainer?.current.classList.add('smartplayer--paused');
        }
      }
    } else if (videoContainer?.current) {
      videoContainer?.current.classList.add('smartplayer--paused');
    }

    const urlParameters = Object.fromEntries(new URLSearchParams(window.location.search));
    const eventParameters = urlParameters.events;
    const userEventParameters = eventParameters ? eventParameters.split(',') : [];

    // document.addEventListener('keydown', controlVideoPlayback);

    if (userEventParameters.includes('resize')) {
      window.addEventListener('resize', () => {
        handleEventVideo('resize', hls.media as HTMLVideoElement)
      });
    }

    const customEvent = (eventName: string) => (event: Event) => handleEventVideo(eventName, event?.target as HTMLVideoElement, ((event as any)?.detail || {}));

    userEventParameters
    .forEach(eventName => {
      document.querySelector(`[data-code="${code}"] video`)?.removeEventListener(eventName, customEvent(eventName));
      document.querySelector(`[data-code="${code}"] video`)?.addEventListener(eventName, customEvent(eventName));
    });

    return () => {
      if (hls) {
        hls.media.removeEventListener('play');
        hls.media.removeEventListener('paused');
        hls.media.removeEventListener('ended');
      }

      if (hls?.native) {
        hls.destroy();
      }

      if (userEventParameters.includes('resize')) {
        window.removeEventListener('resize', () => {
          handleEventVideo('resize', hls.media as HTMLVideoElement);
        });
      }

      // document.removeEventListener('keydown', controlVideoPlayback);

      userEventParameters.forEach(eventName => {
        document.querySelector(`[data-code="${code}"] video`)?.removeEventListener(eventName, customEvent(eventName));
      });

      setHls(undefined);

      if (data?.live) {
        hls.off(Hls.Events.ERROR);
      }
    };

  }, [hls]);

  /*****
  ***PLAY BUTTON
  *******/
  const isVideoPlaying = () => {
    return !!(hls.media.currentTime > 0 && !hls.media.paused && !hls.media.ended && hls.media.readyState > 2);
  };

  const playVideo = (forceAudio?: boolean) => {
    const isPlaying = isVideoPlaying();
    const airplayVideo = videoContainer?.current?.querySelector('.smartplayer__airplay') as HTMLVideoElement;

    const video = isAirplayAvailable && isAirplayConnected ? airplayVideo : hls.media;

    if (forceAudio && isPlaying && video.autoplay && (video.volume === 0 || video.muted)) {
      videoContainer?.current?.classList.remove('smartplayer--autoplaying');
      video.volume = 1;
      video.muted = false;
      return;
    } else if (forceAudio && !isPlaying && video.autoplay && (video.volume === 0 || video.muted)) {
      videoContainer?.current?.classList.remove('smartplayer--autoplaying');
      video.volume = 1;
      video.muted = false;
      video.play();
      return;
    }

    if (isPlaying && videoContainer?.current?.classList.contains('smartplayer--autoplaying')) {
      videoContainer?.current?.classList.remove('smartplayer--autoplaying');
      video.muted = false;
      video.volume = 1;
      video.pause();
      return;
    }

    if (isPlaying) {
      video.pause();
      return;
    }

    if (video.ended) {
      video.currentTime = 0;
    }

    video.play();
  };

  function seekVideo(video: HTMLVideoElement, seconds: number) {
    const newTime = video.currentTime + seconds;
    video.currentTime = Math.max(0, Math.min(newTime, video.duration));
  }


  function controlVideoPlayback(event: KeyboardEvent) {
    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    const video = rootElement.querySelector('video') as HTMLVideoElement;

    if (!video) {
      console.error('Elemento de vídeo não encontrado');
      return;
    }

    if ((event.target as any)?.tagName === 'INPUT' || (event.target as any)?.tagName === 'TEXTAREA') {
      return;
    }

    const playPauseKeys = [
      ' ',          // Espaço
      'Enter',      // Enter
      'k',          // Tecla K (comum em players de vídeo)
      'MediaPlayPause', // Tecla de play/pause em teclados de mídia
    ];

    const resumedVideoKeys = ['r'];

    const muteKeys = ['m', 'AudioVolumeMute'];

    const fullscreenKeys = ['f'];

    const seekForwardKeys = ['ArrowRight'];
    const seekBackwardKeys = ['ArrowLeft'];

    const exitScreens = ['Escape'];

    if (playPauseKeys.includes(event.key)) {
      event.preventDefault(); // Previne o comportamento padrão da tecla

      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    } else if (muteKeys.includes(event.key)) {
      event.preventDefault();
      video.muted = !video.muted;
      video.volume = video.muted ? 0 : 1;
    } else if (fullscreenKeys.includes(event.key)) {
      event.preventDefault();
      const btnFullScreen = rootElement.querySelector('.smartplayer__fullscreen > button') as HTMLButtonElement;
      btnFullScreen?.click();
    } else if (seekForwardKeys.includes(event.key)) {
      event.preventDefault();
      seekVideo(video, 10);
    } else if (seekBackwardKeys.includes(event.key)) {
      event.preventDefault();
      seekVideo(video, -10);
    } else if (resumedVideoKeys.includes(event.key) && !event.ctrlKey) {
      event.preventDefault();
      const btnSwitchResumed = rootElement.querySelector('.smartplayer__switchCbx') as HTMLInputElement;
      btnSwitchResumed?.click();
    } else if (exitScreens.includes(event.key)) {
      event.preventDefault();
      setOpenMindMap(false);
      setOpenQuiz(false);
      setOpenSearch(false);
      setOpenSummary(false);
      setOpenSummaryText(false);
    }
  }

  useEffect(() => {
    if (!data || !data?.video || !code) {
      return;
    }

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    const video = rootElement.querySelector('video') as HTMLVideoElement;

    if (!video) {
      return;
    }

    const urlParameters = Object.fromEntries(new URLSearchParams(window.location.search));
    const eventParameters = urlParameters.events;
    const userEventParameters = eventParameters ? eventParameters.split(',') : [];

    // const nativeEvents = [
    //   'canplay',
    //   'canplaythrough',
    //   'durationchange',
    //   'ended',
    //   'error',
    //   'loadedmetadata',
    //   'loadstart',
    //   'pause',
    //   'play',
    //   'playing',
    //   'progress',
    //   'ratechange',
    //   'seeked',
    //   'seeking',
    //   'suspend',
    //   'timeupdate',
    //   'volumechange',
    //   'waiting'
    // ];

    // const customEvents = [
    //   'dispose',
    //   'enterFullWindow',
    //   'fullscreenchange',
    //   'exitFullWindow',
    //   'enterpictureinpicture',
    //   'leavepictureinpicture',
    //   'questionResponse',
    //   'resize',
    //   'summaryChange',
    //   'openSummary',
    //   'openSummaryText',
    //   'openMindMap',
    //   'openQuiz',
    //   'openSearch'
    // ];

    document.addEventListener('keydown', controlVideoPlayback);

    if (userEventParameters.includes('resize')) {
      window.addEventListener('resize', () => {
        handleEventVideo('resize', video as HTMLVideoElement)
      });
    }

    const customEvent = (eventName: string) => (event: Event) => handleEventVideo(eventName, event?.target as HTMLVideoElement, (event as any)?.detail);

    userEventParameters
    .forEach(eventName => {
      document.querySelector(`[data-code="${code}"] video`)?.removeEventListener(eventName, customEvent(eventName));
      document.querySelector(`[data-code="${code}"] video`)?.addEventListener(eventName, customEvent(eventName));
    });

    return () => {
      document.removeEventListener('keydown', controlVideoPlayback);

      userEventParameters.forEach(eventName => {
        document.querySelector(`[data-code="${code}"] video`)?.removeEventListener(eventName, customEvent(eventName));
      });
    };
  }, []);

  const videoElement = useMemo(() => (
    <>
      {data?.video ? (<video className="smartplayer__videoHls" key={data?.code} ref={videoEl} src={`${data?.video}&authorization=${data?.authorization || ''}`} poster={data?.poster} crossOrigin="anonymous" x-webkit-airplay="deny" x-webkit-wirelessvideoplaybackdisabled="true" preload="auto" playsInline controlsList="nodownload noremoteplayback" tabIndex={-1} muted={data?.config?.muted} loop={data?.config?.loop} autoPlay={data?.config?.autoplay} disableRemotePlayback={true}>
        <source src={`${data?.video || ''}&authorization=${data?.authorization || ''}`} type="application/x-mpegURL"></source>
      </video>) : null}
      {
        canAirplay ? (
          <video className="smartplayer__airplay" title={data?.name} key={data?.code + '--airplay'} src={`${data?.video}&authorization=${data?.authorization || ''}`} poster={data?.poster} crossOrigin="anonymous" x-webkit-airplay="allow" preload="metadata" playsInline controlsList="nodownload" tabIndex={-1}  disableRemotePlayback={false} />
        ) : null
      }
    </>
  ), [videoEl, data]);

  /*
  <video crossOrigin="anonymous" x-webkit-airplay="allow" controlsList="nodownload" tabIndex={-1} className="video--native" poster={data?.poster} src={data?.video}>
    {(data?.subtitles || []).map((subTitle) => (
      <track id={`subtitle--${subTitle.language}`} kind="subtitles" src={subTitle.src} srcLang={subTitle.language} label={subTitle.label} key={subTitle.language} />
    ))}
  </video>
  */

  return (
    <>
      <EmbedStyl className="smartplayer" ref={videoContainer} primaryColor={data?.primaryColor} textColor={data?.textColor}>
        <div className="smartplayer__container">
          <div className={`smartplayer__video ${isBuffering ? 'smartplayer--loading' : 'smartplayer--not-loading'} ${isAirplayConnected ? 'smartplayer--airplay-connected' : ''}`}>
            {
              currentLevel != null && levels != null && levels[currentLevel] ?
              (
                <div className="smartplayer__videoResolution">
                  {
                    `${levels[currentLevel]?.height}p`
                  }
                </div>
              ) : null
            }
            {
              videoElement
            }
            <div className="smartplayer__loader">
              <Loader
                model="round"
                variant="currentcolor"
              />
            </div>
          </div>
          <div className="smartplayer__front">
            <ClickableArea
              hls={hls}
              videoContainer={videoContainer}
              code={data?.code || ''}
              keepWatching={!!data?.config?.keepWatching}
              autoplay={!!data?.config?.autoplay}
              bigplay={!!data?.config?.bigPlay}
              playVideo={playVideo}
              skipIntro={
                typeof data?.config?.skipIntro === 'number' && !isNaN(data?.config?.skipIntro)
                  ? data?.config?.skipIntro
                  : null
              }
              ctas={ctas}
              enableCta={ctas && ctas?.length > 0}
            />
            <div dir="auto" className="smartplayer__captions">
            </div>
            <ControlBar
              code={data?.code || ''}
              currentLevel={currentLevel}
              levels={levels}
              hls={hls}
              duration={duration}
              videoContainer={videoContainer}
              playVideo={playVideo}
              data={data}
              onLoadAI={onLoadAI}
              chapters={chapters}
              summaryState={{openSummary, setOpenSummary}}
              summaryTextState={{openSummaryText, setOpenSummaryText}}
              mindMapState={{openMindMap, setOpenMindMap}}
              quizState={{openQuiz, setOpenQuiz}}
              searchState={{openSearch, setOpenSearch}}
              aiProductsState={{aiProducts, setAIProducts}}
              airplayAvailable={{isAirplayAvailable, setIsAirplayAvailable}}
              airplayConnected={{isAirplayConnected, setIsAirplayConnected}}
              attachments={attachments}
            />
          </div>
        </div>
      </EmbedStyl>
      <div className="smartplayer__ai">
        <Search
          hls={hls}
          primaryColor={data?.primaryColor}
          textColor={data?.textColor || ''}
          mediaCode={data?.code || ''}
          authorization={data?.authorization}
          opened={openSearch} onClose={() => setOpenSearch(false)}
          hasAIOpened={openSummary || openMindMap || openQuiz || openSearch}
        />
        <MindMap
          text={aiProducts?.mindMap || ''}
          opened={openMindMap}
          onClose={() => setOpenMindMap(false)}
        />
        <SummaryText
          type="SUMMARY"
          mediaCode={code || ''}
          authorization={data?.authorization || ''}
          text={aiProducts?.summary || ''}
          opened={openSummary}
          onClose={() => setOpenSummary(false)}
        />
        <SummaryText
          type="SUMMARY_TEXT"
          text={aiProducts?.summaryText || ''}
          opened={openSummaryText}
          onClose={() => setOpenSummaryText(false)}
        />
        {
          (aiProducts?.questions || []).length > 0 ? (
            <Quiz
              questions={aiProducts?.questions || []}
              opened={openQuiz}
              onClose={() => setOpenQuiz(false)}
            />
          ) : null
        }

      </div>
    </>
  );
};

export default Embed2;
